import { useState, type ReactNode } from 'react';
import { DRING_TON, dringText, plusTageIso, tagText, type Aufgabe } from '../../../shared/crm';
import { api } from '../../api';
import { SeitenKopf, Symbol, useHinweis } from '../../komponenten/Grundbausteine';
import { useDaten, zaehlerNeu } from '../../daten';
import { gehe } from '../../ort';

// Gemeinsame Bausteine des CRM (Prototyp head, aufgTabelle, Filter-Chips)

export const crmAktion = <T = Record<string, unknown>>(name: string, daten: unknown) => api<T>(`/api/crm/aktion/${name}`, { methode: 'POST', daten });

/** Führt eine Aktion des CRM aus und meldet das Ergebnis als Hinweis; gibt das Ergebnis zurück oder null bei Fehler. */
export async function crmTun<T extends Record<string, unknown> = Record<string, unknown>>(hinweis: (t: string) => void, name: string, daten: unknown, nachher?: (e: T) => void): Promise<T | null> {
  try {
    const r = await crmAktion<T & { text?: string }>(name, daten);
    if (r.text) hinweis(r.text);
    nachher?.(r);
    return r;
  } catch (e) {
    hinweis(`Geht nicht: ${(e as Error).message}`);
    return null;
  }
}

/** Seitenkopf des CRM (Prototyp head: h1#h-crm, Zeile darunter, rechts ein Element). */
export const Kopf = ({ titel, meta, children }: { titel: string; meta: ReactNode; children?: ReactNode }) => <SeitenKopf titel={titel} zusatz={meta} id="h-crm">{children}</SeitenKopf>;

/** Filter-Chips mit Zählern (Prototyp chips filt). */
export function Chips<T extends string>({ wahl, wert, setzen, attr }: { wahl: [T, string, number][]; wert: T; setzen: (w: T) => void; attr: string }) {
  return <div className="chips">{wahl.map(([k, l, n]) => <button key={k} className="chip filt" {...{ [`data-${attr}`]: k }} aria-pressed={wert === k} onClick={() => setzen(k)}>{l} {n}</button>)}</div>;
}

/** Wohin eine Aufgabe führt. */
export function zurAufgabe(a: Aufgabe) {
  const z = a.ziel;
  if (z.art === 'anruf') gehe(`/crm/kampagnen/${z.kampagne}?zeile=${z.zeile}`);
  else if (z.art === 'nachhaken') gehe(`/crm/kampagnen/aufgabe/${z.kid}?art=${encodeURIComponent(a.art)}`);
  else if (z.art === 'chat') gehe(`/crm/nachrichten/${z.chat}`);
  else if (z.art === 'kunde') gehe(`/crm/kunden/${z.kid}`);
  else gehe(z.rid ? `/buchhaltung/rechnungen?r=${z.rid}` : `/buchhaltung/${z.go ?? 'uebersicht'}`);
}

/** Tabelle der Aufgaben (Prototyp aufgTabelle): Dringlichkeit, Aufgabe, Worum es geht, Offen seit. Wiedervorlagen lassen sich weitergeben (mit neu). */
/**
 * Schnell erledigen, ohne die Kundenansicht zu öffnen (Leon 2026-10-30): Wiedervorlage erledigt oder verschoben;
 * Vertragsphase mit kurzer Notiz und Wiedervorlage, dann ruht die Aufgabe bis dahin; Vertragsaufgabe erledigt.
 */
function Schnell({ t, fertig }: { t: Aufgabe; fertig: () => void }) {
  const hinweis = useHinweis();
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState('');
  const [datum, setDatum] = useState(plusTageIso(heuteIso(), 7));
  const halt = { onClick: (e: { stopPropagation: () => void }) => e.stopPropagation(), onKeyDown: (e: { stopPropagation: () => void }) => e.stopPropagation() };
  const wv = /^v\d+$/.exec(t.id) ? Number(t.id.slice(1)) : null, va = /^va\d+$/.exec(t.id) ? Number(t.id.slice(2)) : null;
  const vertrag = /^v[ph]/.test(t.id) && t.ziel.art === 'kunde' ? t.ziel.kid : null;
  if (wv) return <span className="schnell" {...halt}>
    <button className="btn btn-sm" data-wvok={wv} onClick={() => crmTun(hinweis, 'wv_erledigt', { wv }, fertig)}>Erledigt</button>
    <button className="btn btn-sm btn-ghost" data-wvplus={wv} onClick={() => crmTun(hinweis, 'wv_verschieben', { wv, datum: plusTageIso(heuteIso(), 7) }, fertig)}>In einer Woche</button>
    <input className="input plain" type="date" aria-label="Wiedervorlage verschieben auf" min={heuteIso()} onChange={e => { if (e.target.value) void crmTun(hinweis, 'wv_verschieben', { wv, datum: e.target.value }, fertig); }} /></span>;
  if (va) return <span className="schnell" {...halt}><button className="btn btn-sm" data-vaok={va} onClick={() => crmTun(hinweis, 'vaufgabe_erledigt', { aufgabe: va }, fertig)}>Erledigt</button></span>;
  if (!vertrag) return null;
  if (!offen) return <span className="schnell" {...halt}><button className="btn btn-sm" data-vwv={vertrag} onClick={() => setOffen(true)}>Notiz und Wiedervorlage</button></span>;
  return <span className="schnell" {...halt}>
    <input className="input plain" style={{ minWidth: 220 }} value={text} placeholder="z. B. angerufen, in einer Woche wieder melden" onChange={e => setText(e.target.value)} />
    <input className="input plain" type="date" aria-label="Wiedervorlage am" min={heuteIso()} value={datum} onChange={e => setDatum(e.target.value)} />
    <button className="btn btn-sm btn-primary" onClick={() => { if (!text.trim()) { hinweis('Bitte kurz eintragen, was Sache ist.'); return; } void crmTun(hinweis, 'notiz', { kunde: vertrag.split('?')[0], text: text.trim(), datum }, fertig); }}>Speichern</button>
    <button className="btn btn-sm btn-ghost" onClick={() => setOffen(false)}>Abbrechen</button></span>;
}

export function AufgabenTabelle({ liste, mitPerson = false, oeffnen = zurAufgabe, neu }: { liste: Aufgabe[]; mitPerson?: boolean; oeffnen?: (a: Aufgabe) => void; neu?: () => void }) {
  const hinweis = useHinweis();
  const weitergeben = (t: Aufgabe, an: number) => crmTun(hinweis, 'wv_weitergeben', { wv: Number(t.id.slice(1)), an }, () => { neu?.(); zaehlerNeu(); });
  return (
    <div className="t-wrap"><table className="t k-list"><thead><tr><th>Dringlichkeit</th>{mitPerson && <th>Person</th>}<th>Aufgabe</th><th className="t-hide-sm">Worum es geht</th><th className="r">Offen seit</th></tr></thead><tbody>
      {liste.length ? liste.map(t => (
        <tr key={t.id + (t.wer ?? '')} data-task={t.id} tabIndex={0} onClick={() => oeffnen(t)} onKeyDown={e => { if (e.key === 'Enter') oeffnen(t); }}>
          <td><span className={`state ${DRING_TON[t.dring]}`}>{dringText(t, tagText)}</span></td>
          {mitPerson && <td>{t.wer}</td>}
          <td><span style={{ fontWeight: 500 }}>{t.art}</span><span className="sub">{t.firma}</span>
            {neu && t.weiter && t.weiter.length > 0 && <select className="input plain" data-wvweiter={t.id} aria-label="Wiedervorlage weitergeben an" value="" style={{ height: 28, width: 'auto', marginTop: 4, fontSize: 'var(--fs-meta)' }}
              onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} onChange={e => { if (e.target.value) void weitergeben(t, Number(e.target.value)); }}>
              <option value="">Weitergeben an …</option>{t.weiter.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
            {neu && <Schnell t={t} fertig={() => { neu(); zaehlerNeu(); }} />}</td>
          <td className="t-hide-sm" style={{ whiteSpace: 'normal', maxWidth: 420 }}>{t.kontext}{t.link && <> <a href={t.link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>Vertrag öffnen</a></>}</td>
          <td className="r">{t.seit}</td>
        </tr>)) : <tr><td colSpan={mitPerson ? 5 : 4} className="meta">Nichts offen.</td></tr>}
    </tbody></table></div>
  );
}

export const Zurueck = ({ text, los }: { text: string; los: () => void }) => <button className="back" data-cback onClick={los}><Symbol id="chev" />{text}</button>;

/** „Mi 21.10., 09:40“ für den Kopf */
export function jetztText(): string {
  const d = new Date();
  const iso = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(d);
  return `${tagText(iso)}, ${new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' }).format(d)}`;
}
export const heuteIso = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date());

/** Eigene Buchungsseiten (Google-Terminbuchung): öffnen und Link kopieren; ohne Seiten nichts */
export interface Buchungsseite { id: number; nutzer: number; titel: string; link: string; angebot: string }
export function MeineBuchungsseiten() {
  const d = useDaten<Buchungsseite[]>('/api/crm/buchungsseiten');
  const hinweis = useHinweis();
  const [kopiert, setKopiert] = useState<number | null>(null);
  const S = d.daten ?? [];
  if (!S.length) return null;
  const kopieren = (s: Buchungsseite) => navigator.clipboard.writeText(s.link).then(() => { setKopiert(s.id); hinweis(`Link „${s.titel}“ kopiert.`); }, () => hinweis('Kopieren ging nicht; bitte den Link öffnen und aus der Adresszeile kopieren.'));
  return (
    <section className="panel" aria-labelledby="h-bs"><div className="panel-head"><div><h2 id="h-bs">Deine Buchungsseiten</h2><p className="meta">Google-Terminbuchung: an Interessenten schicken oder selbst einbuchen</p></div></div>
      <div className="t-wrap"><table className="t"><tbody>{S.map(s => <tr key={s.id}><td><span style={{ fontWeight: 500 }}>{s.titel}</span><span className="sub">{s.angebot ? `Terminlink der Mailkampagnen ${s.angebot}` : s.link}</span></td>
        <td className="r"><div className="assign" style={{ justifyContent: 'flex-end' }}><a className="btn btn-sm" data-bso={s.id} href={s.link} target="_blank" rel="noopener noreferrer">Öffnen</a>
          <button className="btn btn-sm" data-bsk={s.id} onClick={() => void kopieren(s)}>{kopiert === s.id ? 'Kopiert' : 'Link kopieren'}</button></div></td></tr>)}</tbody></table></div></section>
  );
}
